import { useIntl } from 'react-intl';
import cx from 'clsx';
import { Disclaimer } from '@repo/design-system-kit';
import type { Amount } from 'app/react/types/amount';
import { useHeaderData } from './internal/use-header-data';
import styles from './header.module.css';

interface HeaderProps {
  organizationName: string;
  organizationLogoUrl?: string;
  amount: Amount;
  reference?: string;
  dueDate: string;
  isRecurring: boolean;
  isOverdue?: boolean;
  recurringFrequency?: string;
  recurringInterval?: number;
  recurringUnit?: string;
}

export function Header({
  organizationName,
  organizationLogoUrl,
  amount,
  reference,
  dueDate,
  isRecurring,
  isOverdue,
  recurringFrequency,
  recurringInterval,
  recurringUnit,
}: HeaderProps) {
  const intl = useIntl();

  const hasLogo = Boolean(organizationLogoUrl);

  const {
    title,
    frequency,
    dueDate: formattedDueDate,
    disclaimer,
    showDisclaimer,
    formattedAmount,
  } = useHeaderData({
    amount,
    dueDate,
    isRecurring,
    isOverdue,
    recurringFrequency,
    recurringInterval,
    recurringUnit,
  });

  return (
    <div>
      <div className={styles['headings-container']}>
        <div>
          <h1 className={cx(styles['page-title'], 'title-1')}>{title}</h1>

          <h2 className="title-2">{organizationName}</h2>
        </div>

        {hasLogo ? (
          <div className={styles.logo}>
            <img alt="" data-testid="organization-logo" src={organizationLogoUrl} />
          </div>
        ) : null}
      </div>

      <div className={cx(styles.amount, 'title-2')} data-testid="amount">
        {formattedAmount}
        <span className="title-4">{frequency}</span>
      </div>

      <ul className={styles['features-list']}>
        {reference ? (
          <li className="title-4" data-testid="reference">
            {intl.formatMessage({ id: 'labels.colon.reference' })} {reference}
          </li>
        ) : null}

        <li className="title-4" data-testid="due-date">
          {intl.formatMessage({ id: 'labels.colon.due_date' })} {formattedDueDate}
        </li>
      </ul>

      {showDisclaimer ? (
        <Disclaimer.Inline data-testid="overdue-disclaimer" level="info">
          <span>{disclaimer}</span>
        </Disclaimer.Inline>
      ) : null}
    </div>
  );
}
